import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon3D from './Icon3D';
import { LocationSearchBox } from './components';
import { ALL_PLACES, INDEXED_PLACES, pathForPlace } from './seoPlaces';

/**
 * The website home page.
 *
 * <p>Redesigned 2026-10-02 after the editorial, photo-led layout of
 * myadventure.in: one oversized tight-tracked headline in a soft hero card, a
 * pill search bar, a bordered "pick your kind" icon row, horizontally scrolling
 * photo rails, an editorial split, a spotlight row with a trust stack, and a
 * tabbed link directory at the foot. The previous version is kept at
 * design-assets/homepage-backup-2026-10-02/HomePage.js (the one before that at
 * design-assets/homepage-backup-2026-09-14/).
 *
 * <p><b>One palette.</b> White page, a #F5F8FE tint for panels, and the single
 * brand blue gradient for the one accent band. No dark navy bands and no extra
 * accent hues — colour comes from the photographs.
 *
 * <p><b>This page IS the SEO.</b> scripts/prerender.js bakes whatever renders
 * here into build/index.html. The FAQ must stay word for word in step with the
 * FAQPage structured data in public/index.html, and every directory tab is in
 * the DOM (only visually hidden) so crawlers follow all the place links.
 *
 * <p><b>Every number and quote here has to be true.</b> No testimonials, user
 * counts or star ratings. The Rent Insights figures are labelled as an example
 * reading for a named locality.
 *
 * <p><b>Motion must never cost content.</b> The reveal class is attached at
 * runtime only for a real browser that has not asked for reduced motion.
 * Puppeteer sets navigator.webdriver, so the prerender is captured fully
 * visible. No gradient text (background-clip:text clipped glyph tails in the
 * last design) — the accent line of the headline is plain blue.
 *
 * <p><b>Asset credits.</b> Photos in public/photos/home2/ are from Unsplash
 * (Unsplash License, no attribution required; credited at the foot anyway).
 * City photos are in public/cities/.
 */

const asset = (p) => `${process.env.PUBLIC_URL || ''}${p}`;
const photo = (name) => asset(`/photos/home2/${name}.jpg`);

/* ── Palette (JS side — for inline values the stylesheet can't reach) ───────── */
const BLUE = '#4285F4';
const BLUE_D = '#2B6ED9';
const NAVY = '#1E3A8A';
const INK = '#0F172A';
const MUTED = '#5B6576';
const FAINT = '#94A3B8';
const LINE = '#E3EAF5';
const TINT = '#F5F8FE';

/* ── Content ────────────────────────────────────────────────────────────────── */

const CITIES = [
    { name: 'Bangalore', file: 'bangalore', tag: 'Tech parks & cafés' },
    { name: 'Delhi', file: 'delhi', tag: 'Metro on every line' },
    { name: 'Mumbai', file: 'mumbai', tag: 'Rooms near the local' },
    { name: 'Hyderabad', file: 'hyderabad', tag: 'HITEC City & beyond' },
    { name: 'Pune', file: 'pune', tag: 'Students & startups' },
    { name: 'Chennai', file: 'chennai', tag: 'OMR to the coast' },
    { name: 'Gurgaon', file: 'gurgaon', tag: 'Cyber City commutes' },
    { name: 'Noida', file: 'noida', tag: 'Sectors near the metro' },
    { name: 'Kolkata', file: 'kolkata', tag: 'Salt Lake & New Town' },
    { name: 'Ahmedabad', file: 'ahmedabad', tag: 'SG Highway & more' },
];

const KINDS = [
    { icon: 'flats', label: 'Flats', route: 'dashboard' },
    { icon: 'rooms', label: 'Rooms', route: 'dashboard' },
    { icon: 'pg', label: 'PG', route: 'dashboard' },
    { icon: 'insights', label: 'Rent Insights', route: 'rentInsights' },
    { icon: 'bell', label: 'Post a requirement', route: 'postRequirement' },
    { icon: 'hero-home', label: 'List your place', route: 'postListing' },
    { icon: 'all', label: 'See everything', route: 'dashboard' },
];

/** Ways to live. These open the search — they are not listings, so no prices. */
const COLLECTIONS = [
    { img: 'flat-green', title: 'Whole flats', sub: '1, 2 and 3 BHK — a place to yourself', alt: 'Bright living room of a rental flat with a green sofa and large windows' },
    { img: 'bedroom', title: 'Private rooms', sub: 'Your own room in a shared flat', alt: 'Furnished bedroom with a double bed, warm lighting and a plant' },
    { img: 'pg-bunk', title: 'PGs & hostels', sub: 'Twin and triple sharing, often with meals', alt: 'Hostel room with bunk beds beside a window' },
    { img: 'studio', title: 'Studios', sub: 'Compact, furnished, ready to move in', alt: 'Compact furnished studio apartment with a kitchenette and a bed' },
    { img: 'students', title: 'Flatmates for students', sub: 'Share near campus and split the rent', alt: 'Group of young friends laughing together in a living room' },
    { img: 'pro-laptop', title: 'Working professionals', sub: 'Quiet, Wi-Fi, close to the office', alt: 'Young woman working on a laptop at home beside a window' },
    { img: 'couple-moving', title: 'Moving in together', sub: 'Flats for couples and small families', alt: 'Couple carrying moving boxes into a new home' },
];

const SPOTLIGHT = [
    {
        img: 'keys', alt: 'A house key being handed over at an apartment door',
        title: 'Zero brokerage, zero commission',
        body: 'There is no broker, so there is nothing to pay one. FlatLoop takes no cut of your rent and no fee on a completed deal.',
        cta: 'Start browsing', route: 'dashboard',
    },
    {
        img: 'woman-phone', alt: 'Young woman smiling while talking on her phone',
        title: 'Talk to the owner directly',
        body: 'Message the person who owns the place — ask about the water supply, the landlord, the actual move-in date.',
        cta: 'How Prime works', route: 'pricing',
    },
    {
        img: 'man-phone', alt: 'Young man laughing on a phone call',
        title: 'Every account verified by phone',
        body: 'No account can post a listing or send a message until its number is verified by OTP, so the other side is always reachable.',
        cta: 'Post your place', route: 'postListing',
    },
];

const TRUST = [
    { icon: 'insights', title: 'Know the fair rent', link: 'Rent Insights', route: 'rentInsights' },
    { icon: 'metro', title: 'Walking distance to the metro', link: 'On every listing', route: 'dashboard' },
    { icon: 'verified', title: 'Lifestyle match', link: 'Veg, pets, WFH & more', route: 'dashboard' },
];

const STEPS = [
    { n: '01', title: 'Search your area', body: 'Pick a locality or drop a pin. Filter by budget, gender preference, furnishing and occupancy.' },
    { n: '02', title: 'Compare honestly', body: 'Every listing shows amenities, walking distance to the metro, grocery and hospital, and how it matches your lifestyle.' },
    { n: '03', title: 'Talk and move in', body: 'Unlock the number with Prime, chat directly, visit, and settle it with the owner. No middleman at any point.' },
];

/** Distances are a real reading for the Nangloi Extension example listing. */
const NEARBY = [
    { icon: 'hospital', label: 'Hospital', m: 9 },
    { icon: 'restaurant', label: 'Restaurant', m: 9 },
    { icon: 'atm', label: 'ATM', m: 127 },
    { icon: 'gym', label: 'Gym', m: 178 },
    { icon: 'pharmacy', label: 'Pharmacy', m: 276 },
    { icon: 'grocery', label: 'Grocery', m: 277 },
    { icon: 'metro', label: 'Metro', m: 735 },
];

/**
 * Free vs Prime. Mirrors the rules the backend enforces: replying is free and
 * only starting a conversation needs Prime, and Prime does not override an
 * owner who has hidden their number.
 */
const PLAN_FREE = [
    'Search and browse every flat, room and PG',
    'Post your own listing — it goes live immediately',
    'Post a requirement so owners can find you',
    'Reply to anyone who messages you',
    'Nearby places and lifestyle match on every listing',
];

const PLAN_PRIME = [
    'Everything in Free',
    'See the owner’s phone number, unless they have chosen to hide it',
    'Start a chat with any owner directly',
    'One-time payment, no auto-renewal',
];

/* Keep in step with the FAQPage JSON-LD in public/index.html. */
const FAQ = [
    { q: 'What does FlatLoop cost?', a: 'Searching, browsing listings and posting your own flat, room or PG are free, and there is zero brokerage and zero commission on any deal. To see an owner’s phone number and chat with them directly, FlatLoop Prime is ₹99 for 30 days — a one-time payment with no auto-renewal.' },
    { q: 'How are users verified on FlatLoop?', a: 'Every account is verified by phone OTP before it can post a listing or send a message, so you are dealing with a reachable person. FlatLoop does not inspect properties in person.' },
    { q: 'Which cities does FlatLoop cover?', a: 'Ten cities: Bangalore, Delhi, Mumbai, Hyderabad, Pune, Chennai, Gurgaon, Noida, Kolkata and Ahmedabad.' },
    { q: 'Can I list my flat, room or PG on FlatLoop?', a: 'Yes, and posting is free. Register, verify your phone number, and post your flat, room or PG. The listing goes live immediately and you keep dealing with tenants directly.' },
    { q: 'How do I find a flatmate in Delhi?', a: 'Open flatloop.in or the FlatLoop Android app, search Delhi or a specific locality, and filter by budget, gender preference, furnishing and occupancy. You contact the owner or tenant directly — there is no broker in between and no brokerage to pay.' },
    { q: 'How do I find a roommate in Bangalore?', a: 'Search Bangalore on FlatLoop and filter by locality, budget, gender preference and occupancy. If nothing matches, post a requirement describing what you are looking for and flat owners will find you instead.' },
    { q: 'Does FlatLoop show what rent is normal for an area?', a: 'Yes. Rent Insights reports typical rents by area, built from rents that FlatLoop users have contributed, so you can tell whether a listing is priced fairly before you contact anyone.' },
];

/* ── Styles ─────────────────────────────────────────────────────────────────── */

/**
 * One scoped stylesheet rather than inline styles, because inline styles cannot
 * express a hover, a media query or a scroll-snap rail. Scoped under .hp so
 * nothing leaks into the rest of the site.
 */
const CSS = `
.hp {
  --blue:${BLUE}; --blue-d:${BLUE_D}; --navy:${NAVY}; --ink:${INK}; --muted:${MUTED}; --faint:${FAINT};
  --line:${LINE}; --tint:${TINT}; --soft:#EAF1FE;
  --grad:linear-gradient(135deg, ${NAVY} 0%, ${BLUE_D} 55%, ${BLUE} 100%);
  --shadow:0 1px 2px rgba(15,23,42,.04), 0 10px 30px -18px rgba(30,58,138,.28);
  --shadow-lg:0 2px 6px rgba(15,23,42,.05), 0 34px 70px -34px rgba(30,58,138,.45);
  --ease:cubic-bezier(.22,1,.36,1);
  background:#fff; color:var(--ink);
}
.hp *, .hp *::before, .hp *::after { box-sizing:border-box; }
.hp [id] { scroll-margin-top:90px; }
/* Content is at most 1400px, with a gutter that grows with the screen
   (16px on phones, 40px on desktop). TopNav in App.js uses the same numbers so
   the nav and the page share one left and right edge. */
.hp-wrap { --gutter:clamp(16px,3vw,40px); max-width:calc(1400px + 2 * var(--gutter)); margin:0 auto; padding-inline:var(--gutter); }
.hp-sec { padding-block:72px; }
@media (max-width:760px) { .hp-wrap { padding-inline:16px; } .hp-sec { padding-block:48px; } }

/* ── Reveal (opt-in, see useMotionEnabled) ── */
.hp-anim .hp-reveal { opacity:0; transform:translateY(24px); }
.hp-anim .hp-reveal.hp-in { opacity:1; transform:none;
  transition:opacity .8s var(--ease), transform .8s var(--ease); transition-delay:var(--hp-d,0ms); }
@keyframes hpFloat { 0%,100% { transform:translateY(0) } 50% { transform:translateY(-8px) } }
.hp-anim .hp-float { animation:hpFloat 6s ease-in-out infinite; }

/* ── Type ── */
.hp-h2 { margin:0; font-size:clamp(28px,3.4vw,40px); font-weight:800; letter-spacing:-.035em; line-height:1.1; color:var(--ink); text-wrap:balance; }
.hp-sub { margin:8px 0 0; font-size:15.5px; line-height:1.55; color:var(--muted); }
.hp-head { display:flex; align-items:flex-end; justify-content:space-between; gap:20px; margin-bottom:26px; }
.hp-note { margin:14px 0 0; font-size:12.5px; color:var(--faint); }
.hp-more { display:inline-flex; align-items:center; gap:8px; flex-shrink:0; padding:10px 18px; border-radius:999px; border:1px solid var(--line);
  background:#fff; color:var(--ink); font:700 14px/1 inherit; font-family:inherit; cursor:pointer; text-decoration:none;
  transition:border-color .2s, background .2s, transform .2s var(--ease); }
.hp-more:hover { border-color:var(--blue); background:var(--tint); }
.hp-more svg { transition:transform .2s var(--ease); }
.hp-more:hover svg { transform:translateX(3px); }
.hp-link { display:inline-flex; align-items:center; gap:6px; background:none; border:0; padding:0; color:var(--navy);
  font:800 14.5px/1.2 inherit; font-family:inherit; cursor:pointer; text-decoration:none; }
.hp-link:hover { color:var(--blue-d); text-decoration:underline; text-underline-offset:4px; }

/* ── Buttons ── */
.hp-btn { display:inline-flex; align-items:center; justify-content:center; gap:10px; border:1px solid transparent; border-radius:999px;
  padding:15px 26px; font-family:inherit; font-size:15px; font-weight:800; line-height:1.1; cursor:pointer; text-decoration:none;
  transition:transform .25s var(--ease), box-shadow .25s, background .25s, border-color .25s, color .25s; }
.hp-btn svg { transition:transform .25s var(--ease); }
.hp-btn:hover svg { transform:translateX(4px); }
.hp-btn-primary { background:var(--navy); color:#fff; }
.hp-btn-primary:hover { background:var(--blue-d); transform:translateY(-2px); box-shadow:0 16px 30px -14px rgba(30,58,138,.6); }
.hp-btn-blue { background:var(--blue); color:#fff; box-shadow:0 14px 30px -12px rgba(66,133,244,.7); }
.hp-btn-blue:hover { background:var(--blue-d); transform:translateY(-2px); }
.hp-btn-ghost { background:#fff; color:var(--ink); border-color:var(--line); }
.hp-btn-ghost:hover { border-color:var(--blue); transform:translateY(-2px); }
.hp-btn-white { background:#fff; color:var(--navy); }
.hp-btn-white:hover { transform:translateY(-2px); box-shadow:0 16px 30px -14px rgba(0,0,0,.4); }
.hp-row { display:flex; flex-wrap:wrap; gap:12px; align-items:center; }

/* ── Hero ── */
.hp-hero { padding:96px 0 0; }
/* The hero is sized to sit above the fold on a 1280×720 laptop: headline,
   search and trust line all visible without scrolling. The search lives in
   the text column because it is the page's primary action. */
.hp-hero-card { position:relative; border-radius:32px; overflow:hidden; padding:44px 48px;
  background:
    radial-gradient(60% 80% at 0% 0%, #DCE8FF 0%, rgba(220,232,255,0) 70%),
    radial-gradient(50% 70% at 100% 0%, #CFE6FB 0%, rgba(207,230,251,0) 70%),
    linear-gradient(180deg, #F3F7FF 0%, #FFFFFF 100%);
  border:1px solid #E6EEFB; }
.hp-hero-grid { display:grid; grid-template-columns:minmax(0,1.12fr) minmax(0,.88fr); gap:52px; align-items:stretch; }
.hp-hero-copy { display:flex; flex-direction:column; justify-content:center; min-width:0; }
.hp-ribbon { align-self:flex-start; display:inline-flex; align-items:center; gap:10px; padding:5px 6px 5px 14px; border-radius:999px;
  background:#fff; border:1px solid var(--line); font-size:13px; font-weight:600; color:var(--muted); box-shadow:var(--shadow); white-space:nowrap; }
.hp-ribbon b { padding:4px 10px; border-radius:999px; background:var(--navy); color:#fff; font-size:12px; font-weight:800; }
.hp-h1 { margin:20px 0 0; font-size:clamp(40px,5.4vw,76px); font-weight:800; letter-spacing:-.05em; line-height:1; color:var(--ink); }
/* Each phrase is its own line: never "Skip / the broker". */
.hp-h1 span { display:block; white-space:nowrap; }
.hp-h1 em { font-style:normal; color:var(--blue); }
.hp-hero-p { margin:18px 0 0; max-width:500px; font-size:17.5px; line-height:1.6; color:var(--muted); }

/* Search pill: label + LocationSearchBox (which renders its own button). */
.hp-search { position:relative; z-index:5; margin-top:28px; background:#fff; border:1px solid var(--line); border-radius:22px;
  padding:8px 8px 8px 6px; box-shadow:var(--shadow-lg); transition:box-shadow .25s; }
.hp-search:focus-within { box-shadow:0 0 0 4px rgba(66,133,244,.18), var(--shadow-lg); }
.hp-field-label { display:block; padding:4px 0 0 16px; font-size:11px; font-weight:800; letter-spacing:1.2px; text-transform:uppercase; color:var(--faint); }
.hp-search input { padding:4px 0 10px !important; font-weight:600 !important; }
.hp-search button[aria-label="Search"] { margin-top:-18px; border-radius:16px !important; background:var(--navy) !important; padding:15px 28px !important; }
.hp-search button[aria-label="Search"]:hover { background:var(--blue-d) !important; }
.hp-quick { display:flex; align-items:center; flex-wrap:wrap; gap:8px; margin-top:16px; }
.hp-quick span { font-size:13px; font-weight:600; color:var(--muted); margin-right:2px; }
.hp-quick a { padding:7px 13px; border-radius:999px; background:#fff; border:1px solid var(--line); color:var(--ink); font-size:13px; font-weight:700;
  text-decoration:none; transition:border-color .2s, color .2s, background .2s; }
.hp-quick a:hover { border-color:var(--blue); color:var(--blue-d); background:var(--tint); }
.hp-trustline { display:flex; flex-wrap:wrap; gap:10px 22px; margin-top:24px; padding-top:20px; border-top:1px solid rgba(15,23,42,.07);
  font-size:13.5px; font-weight:600; color:var(--muted); }
.hp-trustline span { display:inline-flex; align-items:center; gap:7px; }

/* Photo fills the column height so the two halves end together. */
.hp-hero-photo { position:relative; min-height:440px; }
.hp-hero-photo > img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; border-radius:26px; display:block; box-shadow:var(--shadow-lg); }
.hp-chip { position:absolute; display:flex; align-items:center; gap:10px; padding:9px 14px 9px 9px; border-radius:16px; background:rgba(255,255,255,.95);
  backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px); box-shadow:0 18px 40px -18px rgba(15,23,42,.45); white-space:nowrap; }
.hp-chip strong { display:block; font-size:13.5px; font-weight:800; color:var(--ink); letter-spacing:-.2px; }
.hp-chip small { display:block; font-size:12px; color:var(--muted); }
.hp-chip-1 { left:-24px; bottom:36px; }
.hp-chip-2 { right:18px; top:18px; }

@media (max-width:1100px) {
  .hp-hero-card { padding:40px 36px; }
  .hp-hero-grid { gap:36px; }
}
@media (max-width:900px) {
  .hp-hero { padding-top:84px; }
  .hp-hero-card { padding:30px 20px 24px; border-radius:26px; }
  .hp-hero-grid { grid-template-columns:1fr; gap:28px; }
  .hp-hero-photo { min-height:0; aspect-ratio:16/10; order:2; }
  .hp-chip-1 { left:12px; bottom:12px; }
  .hp-chip-2 { right:12px; top:12px; }
}
@media (max-width:520px) {
  .hp-hero-card { margin-inline:-6px; }
  .hp-h1 span { white-space:normal; }
  .hp-hero-p { font-size:16px; }
  .hp-ribbon { font-size:11.5px; gap:6px; padding-left:10px; }
  .hp-ribbon b { font-size:11px; padding:4px 8px; }
  .hp-search button[aria-label="Search"] { padding:13px 18px !important; }
  .hp-long { display:none; }
  .hp-quick { flex-wrap:nowrap; overflow-x:auto; scrollbar-width:none; margin-inline:-20px; padding-inline:20px; }
  .hp-quick::-webkit-scrollbar { display:none; }
  .hp-quick a, .hp-quick span { flex-shrink:0; }
  .hp-trustline { gap:10px 16px; font-size:12.5px; }
  .hp-chip { padding:7px 11px 7px 7px; }
  .hp-chip strong { font-size:12px; }
  .hp-chip small { display:none; }
}

/* ── Kinds (icon row in a bordered box) ── */
.hp-kinds { position:relative; border:1px solid var(--line); border-radius:28px; padding:34px 22px 26px; margin-top:8px; }
.hp-kinds-title { position:absolute; top:0; left:50%; transform:translate(-50%,-50%); background:#fff; padding:0 16px;
  font-size:17px; font-weight:800; color:var(--navy); letter-spacing:-.2px; white-space:nowrap; }
.hp-kinds-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:8px; }
.hp-kind { display:flex; flex-direction:column; align-items:center; gap:10px; padding:10px 4px; background:none; border:0; border-radius:18px;
  cursor:pointer; font-family:inherit; color:var(--ink); transition:background .2s; }
.hp-kind:hover { background:var(--tint); }
.hp-kind-ic { width:76px; height:76px; border-radius:50%; display:grid; place-items:center; background:var(--tint); border:1px solid var(--line);
  transition:transform .3s var(--ease), box-shadow .3s; }
.hp-kind:hover .hp-kind-ic { transform:translateY(-4px); box-shadow:var(--shadow); }
.hp-kind span:last-child { font-size:14px; font-weight:700; text-align:center; line-height:1.25; }
@media (max-width:900px) {
  .hp-kinds { padding:30px 0 18px; }
  .hp-kinds-grid { display:flex; overflow-x:auto; scroll-snap-type:x mandatory; padding-inline:14px; scrollbar-width:none; }
  .hp-kinds-grid::-webkit-scrollbar { display:none; }
  .hp-kind { flex:0 0 96px; scroll-snap-align:start; }
  .hp-kind-ic { width:64px; height:64px; }
}

/* ── Rails (horizontal scrolling cards) ── */
.hp-rail-box { position:relative; }
.hp-rail { display:grid; grid-auto-flow:column; gap:18px; overflow-x:auto; scroll-snap-type:x mandatory; scroll-behavior:smooth;
  padding-bottom:6px; scrollbar-width:none; overscroll-behavior-x:contain; }
.hp-rail::-webkit-scrollbar { display:none; }
.hp-rail > * { scroll-snap-align:start; }
.hp-rail-cities { grid-auto-columns:calc((100% - 18px * 4) / 5); }
.hp-rail-cols { grid-auto-columns:calc((100% - 18px * 3) / 4); }
@media (max-width:1100px) { .hp-rail-cities { grid-auto-columns:calc((100% - 18px * 3) / 4); } .hp-rail-cols { grid-auto-columns:calc((100% - 18px * 2) / 3); } }
@media (max-width:760px) { .hp-rail { gap:14px; margin-inline:-16px; padding-inline:16px; scroll-padding-inline:16px; }
  .hp-rail-cities { grid-auto-columns:62%; } .hp-rail-cols { grid-auto-columns:78%; } }
.hp-arrow { position:absolute; top:calc(50% - 40px); z-index:3; width:46px; height:46px; border-radius:50%; display:grid; place-items:center;
  background:#fff; border:1px solid var(--line); color:var(--ink); cursor:pointer; box-shadow:var(--shadow);
  transition:opacity .2s, transform .2s var(--ease); }
.hp-arrow:hover { transform:scale(1.06); }
.hp-arrow[disabled] { opacity:0; pointer-events:none; }
.hp-arrow-l { left:-22px; } .hp-arrow-r { right:-22px; }
@media (max-width:760px) { .hp-arrow { display:none; } }

.hp-city { position:relative; display:block; aspect-ratio:4/5; border-radius:22px; overflow:hidden; text-decoration:none; color:#fff; isolation:isolate; }
.hp-city img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; transition:transform .8s var(--ease); z-index:-1; }
.hp-city::after { content:''; position:absolute; inset:0; background:linear-gradient(180deg, rgba(15,23,42,0) 40%, rgba(15,23,42,.78) 100%); z-index:-1; }
.hp-city:hover img { transform:scale(1.06); }
.hp-city-txt { position:absolute; left:18px; right:18px; bottom:16px; }
.hp-city-txt strong { display:flex; align-items:center; justify-content:space-between; font-size:21px; font-weight:800; letter-spacing:-.4px; }
.hp-city-txt small { display:block; margin-top:3px; font-size:13px; opacity:.85; font-weight:500; }
.hp-areas { margin-top:22px; font-size:14px; line-height:2; color:var(--muted); }
.hp-areas a { color:var(--navy); font-weight:600; text-decoration:none; }
.hp-areas a:hover { text-decoration:underline; }

.hp-col { display:block; width:100%; padding:0; background:none; border:0; text-align:left; cursor:pointer; font-family:inherit; color:var(--ink); }
.hp-col-img { position:relative; aspect-ratio:4/3; border-radius:20px; overflow:hidden; background:var(--tint); }
.hp-col-img img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .8s var(--ease); }
.hp-col:hover .hp-col-img img { transform:scale(1.05); }
.hp-col-go { position:absolute; right:12px; top:12px; width:36px; height:36px; border-radius:50%; display:grid; place-items:center;
  background:rgba(255,255,255,.92); color:var(--ink); transition:background .2s, color .2s; }
.hp-col:hover .hp-col-go { background:var(--navy); color:#fff; }
.hp-col h3 { margin:14px 0 0; font-size:17px; font-weight:800; letter-spacing:-.3px; }
.hp-col p { margin:4px 0 0; font-size:14px; color:var(--muted); line-height:1.45; }

/* ── Editorial split ── */
.hp-edit { display:grid; grid-template-columns:1fr 1.05fr; gap:56px; align-items:center; }
.hp-edit-copy { position:relative; }
.hp-edit-copy::before { content:''; position:absolute; inset:-30% -20% -30% -30%; z-index:-1;
  background:radial-gradient(closest-side, rgba(147,184,248,.38), rgba(147,184,248,0)); }
.hp-edit h2 { margin:0; font-size:clamp(36px,5vw,62px); font-weight:800; letter-spacing:-.045em; line-height:1.02; color:#253043; }
.hp-edit p { margin:22px 0 28px; max-width:470px; font-size:17px; line-height:1.65; color:var(--muted); }
.hp-edit-photos { position:relative; }
.hp-edit-photos img:first-child { width:100%; aspect-ratio:3/2; object-fit:cover; border-radius:26px; display:block; box-shadow:var(--shadow-lg); }
.hp-edit-photos img:last-child { position:absolute; width:36%; aspect-ratio:3/4; object-fit:cover; left:-9%; bottom:-14%; border-radius:20px;
  border:6px solid #fff; box-shadow:var(--shadow-lg); }
@media (max-width:900px) { .hp-edit { grid-template-columns:1fr; gap:36px; } .hp-edit-photos { margin:0 0 40px 24px; } .hp-edit-photos img:last-child { left:-24px; } }

/* ── Spotlight ── */
.hp-spot { display:grid; grid-template-columns:repeat(3,1fr) .95fr; gap:18px; }
.hp-card { background:#fff; border:1px solid var(--line); border-radius:22px; box-shadow:var(--shadow); }
.hp-spot-card { display:flex; flex-direction:column; overflow:hidden; transition:transform .35s var(--ease), box-shadow .35s; }
.hp-spot-card:hover { transform:translateY(-4px); box-shadow:var(--shadow-lg); }
.hp-spot-card img { width:100%; aspect-ratio:16/11; object-fit:cover; display:block; }
.hp-spot-body { display:flex; flex-direction:column; flex:1; padding:18px 20px 20px; }
.hp-spot-body h3 { margin:0; font-size:17.5px; font-weight:800; letter-spacing:-.3px; }
.hp-spot-body p { margin:8px 0 16px; font-size:14.5px; line-height:1.55; color:var(--muted); flex:1; }
.hp-trust { display:grid; gap:14px; grid-auto-rows:1fr; }
.hp-trust > div { display:flex; }
.hp-trust-item { flex:1; display:flex; align-items:center; gap:14px; padding:18px; text-align:left; width:100%; cursor:pointer; font-family:inherit;
  transition:border-color .2s, transform .25s var(--ease); }
.hp-trust-item:hover { border-color:var(--blue); transform:translateX(3px); }
.hp-trust-ic { width:48px; height:48px; border-radius:14px; display:grid; place-items:center; background:var(--tint); border:1px solid var(--line); flex-shrink:0; }
.hp-trust-item strong { display:block; font-size:15px; font-weight:800; color:var(--ink); }
.hp-trust-item small { display:block; margin-top:3px; font-size:13px; font-weight:700; color:var(--navy); }
@media (max-width:1100px) { .hp-spot { grid-template-columns:repeat(3,1fr); } .hp-trust { grid-column:1/-1; grid-template-columns:repeat(3,1fr); } }
@media (max-width:760px) { .hp-spot { grid-template-columns:1fr; } .hp-trust { grid-template-columns:1fr; } }

/* ── Panel (steps + insights) ── */
.hp-panel { background:var(--tint); border:1px solid #E6EEFB; border-radius:32px; padding:48px; }
.hp-panel-grid { display:grid; grid-template-columns:.9fr 1.1fr; gap:48px; align-items:start; }
.hp-steps { display:grid; gap:14px; }
.hp-step { display:grid; grid-template-columns:auto 1fr; gap:18px; padding:22px 24px; }
.hp-step-n { font-size:34px; font-weight:800; letter-spacing:-.05em; line-height:1; color:var(--blue); }
.hp-step h3 { margin:0; font-size:17.5px; font-weight:800; letter-spacing:-.3px; }
.hp-step p { margin:6px 0 0; font-size:14.5px; line-height:1.55; color:var(--muted); }
.hp-stats { display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-top:26px; }
.hp-stat { padding:16px 18px; }
.hp-stat small { display:block; font-size:12px; font-weight:700; color:var(--faint); text-transform:uppercase; letter-spacing:.8px; }
.hp-stat b { display:block; margin-top:4px; font-size:26px; font-weight:800; letter-spacing:-.04em; color:var(--ink); }
.hp-near { display:flex; flex-wrap:wrap; gap:8px; margin-top:14px; }
.hp-near span { display:inline-flex; align-items:center; gap:7px; padding:7px 12px 7px 8px; border-radius:999px; background:#fff; border:1px solid var(--line);
  font-size:13px; font-weight:700; color:var(--ink); }
.hp-near em { font-style:normal; color:var(--muted); font-weight:600; }
@media (max-width:900px) { .hp-panel { padding:28px 18px; border-radius:26px; } .hp-panel-grid { grid-template-columns:1fr; gap:36px; } }

/* ── Plans ── */
.hp-plans { display:grid; grid-template-columns:1fr 1fr; gap:18px; max-width:980px; margin:0 auto; }
.hp-plan { position:relative; padding:32px; display:flex; flex-direction:column; }
.hp-plan h3 { display:flex; align-items:center; gap:10px; margin:0; font-size:20px; font-weight:800; }
.hp-price { margin-top:14px; font-size:46px; font-weight:800; letter-spacing:-.05em; line-height:1; }
.hp-price small { margin-left:8px; font-size:15px; font-weight:600; color:var(--muted); letter-spacing:0; }
.hp-plan ul { list-style:none; margin:22px 0 26px; padding:0; display:grid; gap:11px; flex:1; }
.hp-plan li { display:flex; gap:10px; align-items:flex-start; font-size:15px; line-height:1.45; color:#334155; }
.hp-plan li svg { flex-shrink:0; margin-top:1px; }
.hp-plan .hp-btn { align-self:flex-start; }
.hp-plan-prime { border:2px solid var(--blue); }
.hp-plan-tag { position:absolute; top:-13px; left:32px; padding:5px 12px; border-radius:999px; background:var(--blue); color:#fff; font-size:12px; font-weight:800; }
@media (max-width:760px) { .hp-plans { grid-template-columns:1fr; } .hp-plan { padding:26px 22px; } }

/* ── FAQ ── */
.hp-faq { display:grid; grid-template-columns:.8fr 1.2fr; gap:48px; align-items:start; }
.hp-faq-side { position:sticky; top:100px; }
.hp-faq-side img { width:100%; aspect-ratio:4/3; object-fit:cover; border-radius:24px; margin-top:26px; display:block; }
.hp-qa { overflow:hidden; }
.hp-qa button { display:flex; align-items:center; justify-content:space-between; gap:16px; width:100%; padding:20px 22px; background:none; border:0;
  cursor:pointer; text-align:left; font-family:inherit; }
.hp-qa h3 { margin:0; font-size:16px; font-weight:800; color:var(--ink); letter-spacing:-.2px; }
.hp-plus { width:30px; height:30px; flex-shrink:0; border-radius:50%; display:grid; place-items:center; background:var(--tint); color:var(--navy);
  transition:transform .3s var(--ease), background .3s, color .3s; }
.hp-open-q .hp-plus { transform:rotate(45deg); background:var(--navy); color:#fff; }
.hp-faq-a { display:grid; grid-template-rows:0fr; transition:grid-template-rows .35s var(--ease); }
.hp-faq-a.hp-open { grid-template-rows:1fr; }
.hp-faq-a > div { overflow:hidden; }
.hp-faq-a p { margin:0; padding:0 22px 20px; font-size:15px; line-height:1.65; color:var(--muted); }
@media (max-width:900px) { .hp-faq { grid-template-columns:1fr; gap:24px; } .hp-faq-side { position:static; } .hp-faq-side img { display:none; } }

/* ── App band (the one blue surface) ── */
.hp-band { position:relative; display:grid; grid-template-columns:1.1fr .9fr; gap:40px; align-items:center; border-radius:32px; overflow:hidden;
  background:var(--grad); color:#fff; padding:52px 56px; }
.hp-band h2 { color:#fff; margin:0; font-size:clamp(28px,3.6vw,44px); font-weight:800; letter-spacing:-.04em; line-height:1.08; }
.hp-band p { margin:14px 0 26px; max-width:520px; font-size:16.5px; line-height:1.6; color:rgba(255,255,255,.85); }
.hp-band > div:first-child { position:relative; z-index:1; }
.hp-band-photo { position:absolute; top:0; right:0; bottom:0; width:46%; }
.hp-band-photo img { width:100%; height:100%; object-fit:cover; display:block;
  -webkit-mask-image:linear-gradient(90deg, transparent 0%, #000 32%); mask-image:linear-gradient(90deg, transparent 0%, #000 32%); }
.hp-store { display:inline-flex; align-items:center; gap:12px; padding:11px 22px 11px 18px; border-radius:16px; text-decoration:none; color:#fff;
  background:rgba(15,23,42,.88); border:1px solid rgba(255,255,255,.18); transition:transform .25s var(--ease), background .25s; }
.hp-store:hover { transform:translateY(-3px); background:#0F172A; }
.hp-band .hp-divider { display:flex; align-items:center; gap:12px; margin:24px 0 16px; font-size:12px; font-weight:800; letter-spacing:1px;
  text-transform:uppercase; color:rgba(255,255,255,.7); }
.hp-band .hp-divider::after { content:''; flex:1; height:1px; background:rgba(255,255,255,.25); }
@media (max-width:900px) {
  .hp-band { grid-template-columns:1fr; padding:36px 22px 0; border-radius:26px; gap:24px; }
  .hp-band-photo { position:relative; width:auto; height:240px; margin:0 -22px; }
  .hp-band-photo img { inset:0; width:100%; height:100%; -webkit-mask-image:linear-gradient(180deg, transparent 0%, #000 35%); mask-image:linear-gradient(180deg, transparent 0%, #000 35%); }
}

/* ── Directory (tabbed links) ── */
.hp-dir-tabs { display:flex; gap:28px; border-bottom:1px solid var(--line); overflow-x:auto; scrollbar-width:none; }
.hp-dir-tabs::-webkit-scrollbar { display:none; }
.hp-dir-tabs button { flex-shrink:0; padding:0 0 14px; margin-bottom:-1px; background:none; border:0; border-bottom:2px solid transparent;
  font-family:inherit; font-size:17px; font-weight:600; color:var(--muted); cursor:pointer; }
.hp-dir-tabs button[aria-selected="true"] { color:var(--ink); font-weight:800; border-bottom-color:var(--ink); }
.hp-dir-grid[hidden] { display:none; }
.hp-dir-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:10px 24px; padding-top:22px; }
.hp-dir-grid a, .hp-dir-grid button { font-size:14px; color:#334155; text-decoration:none; background:none; border:0; padding:0; text-align:left;
  font-family:inherit; cursor:pointer; }
.hp-dir-grid a:hover, .hp-dir-grid button:hover { color:var(--blue-d); text-decoration:underline; }
@media (max-width:900px) { .hp-dir-grid { grid-template-columns:repeat(2,1fr); } }

.hp-credit { margin:30px 0 0; text-align:center; font-size:12px; color:var(--faint); }
.hp-credit a { color:inherit; }

#contact h2 { color:#fff; }

@media (prefers-reduced-motion: reduce) {
  .hp *, .hp *::before, .hp *::after { animation:none !important; transition:none !important; scroll-behavior:auto !important; }
}
`;

/* ── Motion hooks ───────────────────────────────────────────────────────────── */

/** Attach the animation class only for a real, motion-tolerant browser. */
const useMotionEnabled = () => {
    const [on, setOn] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (navigator.webdriver) return;                       // prerender snapshot
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        setOn(true);
    }, []);
    return on;
};

/**
 * One observer for the whole page; elements register themselves through a ref.
 * Nodes that arrive before the observer exists (refs attach before effects
 * run) are parked and observed once it does — without that queue the reveal
 * class is applied and never cleared, and the page renders blank.
 */
const useReveal = (enabled) => {
    const io = useRef(null);
    const pending = useRef(new Set());

    useEffect(() => {
        if (!enabled || typeof IntersectionObserver === 'undefined') return undefined;
        const obs = new IntersectionObserver(
            (entries) => entries.forEach((e) => {
                if (e.isIntersecting) {
                    e.target.classList.add('hp-in');
                    obs.unobserve(e.target);
                }
            }),
            { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
        );
        io.current = obs;
        pending.current.forEach((n) => obs.observe(n));
        pending.current.clear();
        return () => { obs.disconnect(); io.current = null; };
    }, [enabled]);

    return useCallback((node) => {
        if (!node) return;
        if (io.current) io.current.observe(node);
        else pending.current.add(node);
    }, []);
};

const Reveal = ({ watch, delay = 0, as: Tag = 'div', className = '', style, children, ...rest }) => (
    <Tag ref={watch} className={`hp-reveal ${className}`} style={{ '--hp-d': `${delay}ms`, ...style }} {...rest}>
        {children}
    </Tag>
);

/** Counts to a target once visible. Renders the final value without motion. */
const CountUp = ({ to, prefix = '', enabled, duration = 1200 }) => {
    const [n, setN] = useState(to);
    const ref = useRef(null);

    useEffect(() => {
        if (!enabled || !ref.current || typeof IntersectionObserver === 'undefined') return undefined;
        setN(0);
        const el = ref.current;
        const obs = new IntersectionObserver((entries) => {
            if (!entries[0].isIntersecting) return;
            obs.disconnect();
            const t0 = performance.now();
            const tick = (t) => {
                const p = Math.min(1, (t - t0) / duration);
                setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
                if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        }, { threshold: 0.4 });
        obs.observe(el);
        return () => obs.disconnect();
    }, [enabled, to, duration]);

    return <span ref={ref}>{prefix}{n.toLocaleString('en-IN')}</span>;
};

/**
 * A horizontal scroll-snap rail with prev/next buttons. The buttons hide at
 * either end; on touch the rail is simply swiped and the buttons are not shown.
 */
const Rail = ({ className, label, children }) => {
    const ref = useRef(null);
    const [edge, setEdge] = useState({ start: true, end: false });

    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth > el.scrollWidth - 8 });
    }, []);

    useEffect(() => {
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure]);

    const page = (dir) => {
        const el = ref.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
    };

    return (
        <div className="hp-rail-box">
            <button type="button" className="hp-arrow hp-arrow-l" onClick={() => page(-1)} disabled={edge.start} aria-label={`Previous ${label}`}>
                <Arrow size={18} flip />
            </button>
            <div ref={ref} className={`hp-rail ${className}`} onScroll={measure} role="region" aria-label={label} tabIndex={0}>
                {children}
            </div>
            <button type="button" className="hp-arrow hp-arrow-r" onClick={() => page(1)} disabled={edge.end} aria-label={`Next ${label}`}>
                <Arrow size={18} />
            </button>
        </div>
    );
};

/* ── Small pieces ───────────────────────────────────────────────────────────── */

const Arrow = ({ size = 18, flip = false }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={flip ? { transform: 'scaleX(-1)' } : undefined}>
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

const Check = ({ color = BLUE }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="11" fill={color} opacity=".14" />
        <path d="M7.5 12.5l3 3 6-6.5" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Head = ({ title, sub, action, watch }) => (
    <Reveal watch={watch} className="hp-head">
        <div>
            <h2 className="hp-h2">{title}</h2>
            {sub && <p className="hp-sub">{sub}</p>}
        </div>
        {action}
    </Reveal>
);

/**
 * Official store marks. Apple's and Google's badge guidelines require the real
 * logo rather than a text label; neither mark is recoloured or reproportioned.
 */
const AppleMark = () => (
    <svg width="19" height="24" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
);

const GooglePlayMark = () => (
    <svg width="22" height="22" viewBox="0 0 512 512" aria-hidden="true" style={{ flexShrink: 0 }}>
        <path fill="#00A0FF" d="M47.6 0C34.6 6.8 25.9 19.2 25.9 35.3v441.4c0 16.1 8.7 28.5 21.7 35.3l1.5.7 247.3-247.3v-5.8L48.6-.7 47.6 0z" />
        <path fill="#FFBC00" d="M378.8 338.7l-82.4-82.5v-5.8l82.4-82.4 1.9 1.1 97.6 55.4c27.9 15.8 27.9 41.7 0 57.6l-97.6 55.4-1.9.2z" />
        <path fill="#FF3A44" d="M380.7 337.6 296.4 253.3 47.6 502.1c9.2 9.7 24.3 10.9 38.6 1.2l294.5-165.7z" />
        <path fill="#00F076" d="M380.7 174.9 86.2 9.2C71.9.5 56.8 1.7 47.6 11.4l248.8 248.8 84.3-85.3z" />
    </svg>
);

const StoreBadge = ({ href, mark, kicker, name }) => (
    <a className="hp-store" href={href} target="_blank" rel="noopener noreferrer">
        {mark}
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, textAlign: 'left' }}>
            <span style={{ fontSize: '10px', fontWeight: 600, opacity: .75, letterSpacing: '.4px', textTransform: 'uppercase' }}>{kicker}</span>
            <span style={{ fontSize: '16.5px', fontWeight: 800, letterSpacing: '-.35px' }}>{name}</span>
        </span>
    </a>
);

/* ── Page ───────────────────────────────────────────────────────────────────── */

const PLACE_BY_SLUG = ALL_PLACES.reduce((a, p) => { a[p.slug] = p; return a; }, {});
const LOCALITIES = INDEXED_PLACES.filter((p) => p.kind === 'locality');

/** Plain left-click stays client-side; modified clicks keep open-in-new-tab. */
const isPlainClick = (e) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0);

const fmtDistance = (m) => (m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`);

const HomePage = ({ onSearch, onNavigate, onPlace }) => {
    const [openFaq, setOpenFaq] = useState(0);
    const [dirTab, setDirTab] = useState('cities');
    const motion = useMotionEnabled();
    const watch = useReveal(motion);

    const go = (route) => () => onNavigate && onNavigate(route);

    /** Props for a real <a href> to a city or area page that stays client-side. */
    const placeLink = (place) => ({
        href: place ? pathForPlace(place) : '/search',
        /* Thin places are still linked — a visitor clicking Chennai should get
           Chennai — but carry nofollow, and the page itself sends noindex. */
        rel: place && !place.index ? 'nofollow' : undefined,
        onClick: (e) => {
            if (!isPlainClick(e)) return;
            e.preventDefault();
            if (place && onPlace) onPlace(place);
            else if (onNavigate) onNavigate('dashboard');
        },
    });

    const DIR_TABS = [
        ['cities', 'Top cities'],
        ...(LOCALITIES.length ? [['areas', 'Popular areas']] : []),
        ['kinds', 'Ways to rent'],
        ['owners', 'For owners'],
    ];

    return (
        <div className={`hp ${motion ? 'hp-anim' : ''}`}>
            <style>{CSS}</style>

            {/* ── Hero ───────────────────────────────────────────────────────── */}
            {/* A <header>, not a <section>, and the tag is load-bearing:
                index.css adds 80px of top padding to #main-content unless the
                page supplies its own header. */}
            <header className="hp-hero">
                <div className="hp-wrap">
                    <div className="hp-hero-card">
                        <div className="hp-hero-grid">
                            <div className="hp-hero-copy">
                                <Reveal watch={watch} className="hp-ribbon">
                                    Flats, rooms & PGs in 10 cities <b>₹0 brokerage</b>
                                </Reveal>
                                <Reveal watch={watch} as="h1" delay={70} className="hp-h1">
                                    <span>Find your place.</span> <span><em>Skip the broker.</em></span>
                                </Reveal>
                                <Reveal watch={watch} as="p" delay={140} className="hp-hero-p">
                                    Flats, rooms and PGs listed by the people who live there. Talk to them directly
                                    and see what the rent should be before you call.
                                </Reveal>

                                {/* Short placeholder on purpose: longer text was cut off on phones. */}
                                <Reveal watch={watch} delay={200} className="hp-search">
                                    <span className="hp-field-label">Where<span className="hp-long"> do you want to live?</span></span>
                                    <LocationSearchBox onSelect={(loc) => onSearch && onSearch(loc)} placeholder="Locality or city" />
                                </Reveal>
                                <Reveal watch={watch} delay={240} className="hp-quick">
                                    <span>Popular:</span>
                                    {['delhi', 'bangalore', 'mumbai', 'pune', 'hyderabad'].map((slug) => {
                                        const city = CITIES.find((c) => c.file === slug);
                                        return <a key={slug} {...placeLink(PLACE_BY_SLUG[slug])}>{city.name}</a>;
                                    })}
                                </Reveal>

                                <Reveal watch={watch} delay={280} className="hp-trustline">
                                    {[['verified', 'Phone-verified accounts'], ['chat', 'Direct owner chat'], ['insights', 'Community rent data']].map(([ic, t]) => (
                                        <span key={t}><Icon3D name={ic} size={20} />{t}</span>
                                    ))}
                                </Reveal>
                            </div>

                            <Reveal watch={watch} delay={120} className="hp-hero-photo">
                                <img
                                    src={photo('sunlit-living')}
                                    alt="Sunlit living room of a rental flat with a sofa, plants and tall windows"
                                    width="1800"
                                    height="1200"
                                    fetchPriority="high"
                                    decoding="async"
                                />
                                {/* Decorative duplicates of on-page copy. */}
                                <div className="hp-chip hp-chip-1 hp-float" aria-hidden="true">
                                    <Icon3D name="verified" size={32} />
                                    <div><strong>OTP-verified owner</strong><small>Every account</small></div>
                                </div>
                                <div className="hp-chip hp-chip-2 hp-float" aria-hidden="true" style={{ animationDelay: '1.2s' }}>
                                    <Icon3D name="metro" size={32} />
                                    <div><strong>Metro 735 m</strong><small>On every listing</small></div>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </header>

            {/* ── Pick your kind ─────────────────────────────────────────────── */}
            <section className="hp-sec" style={{ paddingBottom: 0 }}>
                <div className="hp-wrap">
                    <Reveal watch={watch} className="hp-kinds">
                        <h2 className="hp-kinds-title">What are you here for?</h2>
                        <div className="hp-kinds-grid">
                            {KINDS.map((k) => (
                                <button key={k.label} type="button" className="hp-kind" onClick={go(k.route)}>
                                    <span className="hp-kind-ic"><Icon3D name={k.icon} size={44} /></span>
                                    <span>{k.label}</span>
                                </button>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Cities rail ────────────────────────────────────────────────── */}
            <section id="cities" className="hp-sec">
                <div className="hp-wrap">
                    <Head
                        watch={watch}
                        title="Find a place in your city"
                        sub="Ten cities, one search — from the metros to the tech corridors."
                        action={<button type="button" className="hp-more" onClick={go('dashboard')}>See all listings <Arrow size={15} /></button>}
                    />
                    <Rail className="hp-rail-cities" label="cities">
                        {CITIES.map((c) => (
                            <a key={c.name} className="hp-city" {...placeLink(PLACE_BY_SLUG[c.file])}>
                                <img src={asset(`/cities/${c.file}.png`)} alt={`Flats, rooms and PGs for rent in ${c.name}`} loading="lazy" decoding="async" />
                                <span className="hp-city-txt">
                                    <strong>{c.name}<Arrow size={18} /></strong>
                                    <small>{c.tag}</small>
                                </span>
                            </a>
                        ))}
                    </Rail>
                </div>
            </section>

            {/* ── Collections rail ───────────────────────────────────────────── */}
            <section className="hp-sec" style={{ paddingTop: 0 }}>
                <div className="hp-wrap">
                    <Head
                        watch={watch}
                        title="Rent the way you live"
                        sub="Whole flats, private rooms and PG beds — filter each by budget, furnishing, gender preference and occupancy."
                        action={<button type="button" className="hp-more" onClick={go('dashboard')}>Browse all <Arrow size={15} /></button>}
                    />
                    <Rail className="hp-rail-cols" label="ways to rent">
                        {COLLECTIONS.map((c) => (
                            <button key={c.title} type="button" className="hp-col" onClick={go('dashboard')}>
                                <span className="hp-col-img" style={{ display: 'block' }}>
                                    <img src={photo(c.img)} alt={c.alt} loading="lazy" decoding="async" />
                                    <span className="hp-col-go"><Arrow size={16} /></span>
                                </span>
                                <h3>{c.title}</h3>
                                <p>{c.sub}</p>
                            </button>
                        ))}
                    </Rail>
                </div>
            </section>

            {/* ── Editorial split ────────────────────────────────────────────── */}
            <section className="hp-sec">
                <div className="hp-wrap hp-edit">
                    <Reveal watch={watch} className="hp-edit-copy">
                        <h2>Feel at home,<br />not at a broker’s office.</h2>
                        <p>
                            Can’t find it? Post a requirement — your budget, the areas you want, who you’d like to
                            live with — and flat and PG owners will get in touch with you instead.
                        </p>
                        <div className="hp-row">
                            <button type="button" className="hp-btn hp-btn-primary" onClick={go('postRequirement')}>
                                Post a requirement <Arrow size={16} />
                            </button>
                            <button type="button" className="hp-btn hp-btn-ghost" onClick={go('postListing')}>
                                I have a property
                            </button>
                        </div>
                    </Reveal>
                    <Reveal watch={watch} delay={120} className="hp-edit-photos">
                        <img src={photo('friends-sofa')} alt="Flatmates relaxing together on a sofa in a shared apartment" loading="lazy" decoding="async" />
                        <img src={photo('balcony')} alt="Small apartment balcony with chairs, a table and potted plants" loading="lazy" decoding="async" />
                    </Reveal>
                </div>
            </section>

            {/* ── Spotlight + trust stack ────────────────────────────────────── */}
            <section id="features" className="hp-sec">
                <div className="hp-wrap">
                    <Head watch={watch} title="Why people rent on FlatLoop" sub="No brokerage, no gatekeeping, and no guessing what a fair rent looks like." />
                    <div className="hp-spot">
                        {SPOTLIGHT.map((s, i) => (
                            <Reveal key={s.title} watch={watch} delay={i * 90} className="hp-card hp-spot-card">
                                <img src={photo(s.img)} alt={s.alt} loading="lazy" decoding="async" />
                                <div className="hp-spot-body">
                                    <h3>{s.title}</h3>
                                    <p>{s.body}</p>
                                    <button type="button" className="hp-link" onClick={go(s.route)}>{s.cta} <Arrow size={15} /></button>
                                </div>
                            </Reveal>
                        ))}
                        <div className="hp-trust">
                            {TRUST.map((t, i) => (
                                <Reveal key={t.title} watch={watch} delay={200 + i * 80}>
                                    <button type="button" className="hp-card hp-trust-item" onClick={go(t.route)}>
                                        <span className="hp-trust-ic"><Icon3D name={t.icon} size={30} /></span>
                                        <span><strong>{t.title}</strong><small>{t.link}</small></span>
                                    </button>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── How it works + Rent Insights panel ─────────────────────────── */}
            <section id="how-it-works" className="hp-sec">
                <div className="hp-wrap">
                    <Reveal watch={watch} className="hp-panel">
                        <div className="hp-panel-grid">
                            <div>
                                <h2 className="hp-h2">Three steps, no middleman</h2>
                                <p className="hp-sub">From search to keys, you deal with one person: the owner.</p>
                                <div className="hp-steps" style={{ marginTop: '26px' }}>
                                    {STEPS.map((s) => (
                                        <div key={s.n} className="hp-card hp-step">
                                            <span className="hp-step-n">{s.n}</span>
                                            <div><h3>{s.title}</h3><p>{s.body}</p></div>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="hp-btn hp-btn-primary" onClick={go('dashboard')} style={{ marginTop: '24px' }}>
                                    Start searching <Arrow size={16} />
                                </button>
                            </div>

                            <div>
                                <h2 className="hp-h2">Know the real rent before you move</h2>
                                <p className="hp-sub">
                                    Rent Insights maps what tenants actually pay, contributed by other FlatLoop users —
                                    so an asking price has something to be judged against.
                                </p>
                                <div className="hp-stats">
                                    {[['Average rent', 9245, '₹'], ['Minimum', 2000, '₹'], ['Maximum', 26000, '₹'], ['Data points', 58, '']].map(([label, to, prefix]) => (
                                        <div className="hp-card hp-stat" key={label}>
                                            <small>{label}</small>
                                            <b><CountUp to={to} prefix={prefix} enabled={motion} /></b>
                                        </div>
                                    ))}
                                </div>
                                <p className="hp-sub" style={{ marginTop: '22px', fontWeight: 700, color: INK }}>And what’s within walking distance:</p>
                                <div className="hp-near">
                                    {NEARBY.map((p) => (
                                        <span key={p.label}><Icon3D name={p.icon} size={22} />{p.label} <em>{fmtDistance(p.m)}</em></span>
                                    ))}
                                </div>
                                <div className="hp-row" style={{ marginTop: '24px' }}>
                                    <button type="button" className="hp-btn hp-btn-blue" onClick={go('rentInsights')}>
                                        View Rent Insights <Arrow size={16} />
                                    </button>
                                    <button type="button" className="hp-btn hp-btn-ghost" onClick={go('contributeRent')}>
                                        Contribute your rent
                                    </button>
                                </div>
                                <p className="hp-note">Example reading: Nangloi Extension, New Delhi.</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Free vs Prime ──────────────────────────────────────────────── */}
            <section className="hp-sec">
                <div className="hp-wrap">
                    <Reveal watch={watch} style={{ textAlign: 'center', marginBottom: '34px' }}>
                        <h2 className="hp-h2">Free to use. ₹99 when you want to reach out.</h2>
                        <p className="hp-sub">No brokerage and no commission, ever. Prime is only for seeing numbers and starting conversations.</p>
                    </Reveal>
                    <div className="hp-plans">
                        <Reveal watch={watch} className="hp-card hp-plan">
                            <h3><Icon3D name="hero-home" size={34} />Free</h3>
                            <div className="hp-price">₹0<small>forever</small></div>
                            <ul>{PLAN_FREE.map((f) => <li key={f}><Check />{f}</li>)}</ul>
                            <button type="button" className="hp-btn hp-btn-ghost" onClick={go('dashboard')}>Start browsing <Arrow size={16} /></button>
                        </Reveal>
                        <Reveal watch={watch} delay={100} className="hp-card hp-plan hp-plan-prime">
                            <span className="hp-plan-tag">FlatLoop Prime</span>
                            <h3><Icon3D name="prime" size={34} />Prime</h3>
                            <div className="hp-price">₹99<small>/ 30 days</small></div>
                            <ul>{PLAN_PRIME.map((f) => <li key={f}><Check color={NAVY} />{f}</li>)}</ul>
                            <button type="button" className="hp-btn hp-btn-blue" onClick={go('pricing')}>See pricing details <Arrow size={16} /></button>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ── FAQ ────────────────────────────────────────────────────────── */}
            <section className="hp-sec">
                <div className="hp-wrap hp-faq">
                    <Reveal watch={watch} className="hp-faq-side">
                        <h2 className="hp-h2">Everything people ask before signing up</h2>
                        <img src={photo('students-out')} alt="Young friends smiling together outdoors" loading="lazy" decoding="async" />
                    </Reveal>
                    <div style={{ display: 'grid', gap: '12px' }}>
                        {FAQ.map((f, i) => {
                            const open = openFaq === i;
                            return (
                                <Reveal key={f.q} watch={watch} delay={i * 40}>
                                    <div className={`hp-card hp-qa ${open ? 'hp-open-q' : ''}`}>
                                        <button type="button" onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open}>
                                            <h3>{f.q}</h3>
                                            <span className="hp-plus" aria-hidden="true">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                                            </span>
                                        </button>
                                        {/* Always in the DOM, only visually collapsed: the FAQPage
                                            structured data needs a visible counterpart. */}
                                        <div className={`hp-faq-a ${open ? 'hp-open' : ''}`}>
                                            <div><p>{f.a}</p></div>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── App band ───────────────────────────────────────────────────── */}
            <section id="download" className="hp-sec" style={{ paddingTop: 0 }}>
                <div className="hp-wrap">
                    <Reveal watch={watch} className="hp-band">
                        <div>
                            <h2>Start looking. It costs nothing.</h2>
                            <p>
                                Search every listing, post your own place, and message anyone who writes to you —
                                all free. Prime is only for reaching out first.
                            </p>
                            <div className="hp-row">
                                <button type="button" className="hp-btn hp-btn-white" onClick={go('dashboard')}>
                                    Browse listings <Arrow size={16} />
                                </button>
                            </div>
                            <div className="hp-divider">Or get the app</div>
                            <div className="hp-row">
                                <StoreBadge href="https://apps.apple.com/in/app/flatloop-flat-room-finder/id6766100303" mark={<AppleMark />} kicker="Download on the" name="App Store" />
                                <StoreBadge href="https://play.google.com/store/apps/details?id=com.flatloop.app&hl=en_IN" mark={<GooglePlayMark />} kicker="Get it on" name="Google Play" />
                            </div>
                        </div>
                        <div className="hp-band-photo">
                            <img src={photo('students')} alt="Friends hanging out together in a shared flat" loading="lazy" decoding="async" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ── Directory ──────────────────────────────────────────────────── */}
            {/* Every panel stays in the DOM (hidden, not unmounted) so the
                prerendered HTML carries all of the place links for crawlers. */}
            <section className="hp-sec" style={{ paddingTop: 0 }}>
                <div className="hp-wrap">
                    <div className="hp-dir-tabs" role="tablist" aria-label="Browse FlatLoop">
                        {DIR_TABS.map(([id, label]) => (
                            <button key={id} type="button" role="tab" id={`hp-tab-${id}`} aria-controls={`hp-panel-${id}`} aria-selected={dirTab === id} onClick={() => setDirTab(id)}>
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="hp-dir-grid" role="tabpanel" id="hp-panel-cities" aria-labelledby="hp-tab-cities" hidden={dirTab !== 'cities'}>
                        {CITIES.map((c) => <a key={c.name} {...placeLink(PLACE_BY_SLUG[c.file])}>Flats & rooms in {c.name}</a>)}
                    </div>
                    {LOCALITIES.length > 0 && (
                        <div className="hp-dir-grid" role="tabpanel" id="hp-panel-areas" aria-labelledby="hp-tab-areas" hidden={dirTab !== 'areas'}>
                            {LOCALITIES.map((p) => <a key={p.slug} {...placeLink(p)}>{p.name}</a>)}
                        </div>
                    )}
                    <div className="hp-dir-grid" role="tabpanel" id="hp-panel-kinds" aria-labelledby="hp-tab-kinds" hidden={dirTab !== 'kinds'}>
                        {['Whole flats', 'Private rooms', 'PGs & hostels', 'Flatmates', 'Studios'].map((t) => <button key={t} type="button" onClick={go('dashboard')}>{t}</button>)}
                        <button type="button" onClick={go('rentInsights')}>Rent Insights</button>
                        <button type="button" onClick={go('postRequirement')}>Post a requirement</button>
                    </div>
                    <div className="hp-dir-grid" role="tabpanel" id="hp-panel-owners" aria-labelledby="hp-tab-owners" hidden={dirTab !== 'owners'}>
                        <button type="button" onClick={go('postListing')}>List your flat — free</button>
                        <button type="button" onClick={go('postListing')}>List your PG — free</button>
                        <button type="button" onClick={go('dashboard')}>See tenant requirements</button>
                        <button type="button" onClick={go('contributeRent')}>Contribute your rent</button>
                        <button type="button" onClick={go('pricing')}>Pricing</button>
                    </div>

                    <p className="hp-credit">
                        Photos from <a href="https://unsplash.com/" target="_blank" rel="noopener noreferrer">Unsplash</a>
                    </p>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
